import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { InitialFocus } from '@actual-app/components/initial-focus';
import { Paragraph } from '@actual-app/components/paragraph';
import { View } from '@actual-app/components/view';

import { Modal, ModalCloseButton, ModalHeader } from '#components/common/Modal';
import { Checkbox } from '#components/forms';
import { useAccounts } from '#hooks/useAccounts';
import type { Modal as ModalType } from '#modals/modalsSlice';

type ConfirmUnlinkAccountModalProps = Extract<
  ModalType,
  { name: 'confirm-unlink-account' }
>['options'];

export function ConfirmUnlinkAccountModal({
  accountName,
  accountId,
  bankId: propBankId,
  accountSyncSource: propAccountSyncSource,
  isViewBankSyncSettings,
  onUnlink,
}: ConfirmUnlinkAccountModalProps) {
  const { t } = useTranslation();
  const { data: accounts = [] } = useAccounts();
  const [removeConnection, setRemoveConnection] = useState(false);

  const targetAccount = accounts.find(a =>
    accountId ? a.id === accountId : a.name === accountName,
  );

  const targetBankId =
    propBankId !== undefined ? propBankId : targetAccount?.bank;
  const syncSource =
    propAccountSyncSource !== undefined
      ? propAccountSyncSource
      : targetAccount?.account_sync_source;

  const isPluggyAi = syncSource === 'pluggyai';
  const remainingAccountsForBank = accounts.filter(
    a =>
      targetBankId &&
      a.bank === targetBankId &&
      a.id !== (accountId || targetAccount?.id),
  );
  const isLastAccountForConnection =
    isPluggyAi && !!targetBankId && remainingAccountsForBank.length === 0;

  return (
    <Modal
      name="confirm-unlink-account"
      containerProps={{ style: { width: '30vw' } }}
    >
      {({ state }) => (
        <>
          <ModalHeader
            title={t('Confirm Unlink')}
            rightContent={<ModalCloseButton onPress={() => state.close()} />}
          />
          <View style={{ lineHeight: 1.5, gap: 12 }}>
            <Paragraph>
              <Trans>
                Are you sure you want to unlink <strong>{accountName}</strong>?
              </Trans>
            </Paragraph>

            <Paragraph>
              {isViewBankSyncSettings
                ? t(
                    'Transactions will no longer be synchronized with this account and must be manually entered. You will not be able to edit the bank sync settings for this account and the settings will close.',
                  )
                : t(
                    'Transactions will no longer be synchronized with this account and must be manually entered.',
                  )}
            </Paragraph>

            {isLastAccountForConnection && (
              <View style={{ marginTop: 4 }}>
                <label
                  htmlFor="remove-connection-checkbox"
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                    userSelect: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <Checkbox
                    id="remove-connection-checkbox"
                    checked={removeConnection}
                    onChange={() => setRemoveConnection(!removeConnection)}
                  />
                  <Trans>
                    This is the last account using this bank connection. Remove
                    connection and credentials from the server as well.
                  </Trans>
                </label>
              </View>
            )}

            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'flex-end',
                marginTop: 8,
              }}
            >
              <Button style={{ marginRight: 10 }} onPress={() => state.close()}>
                <Trans>Cancel</Trans>
              </Button>
              <InitialFocus>
                <Button
                  variant="primary"
                  onPress={() => {
                    onUnlink(removeConnection);
                    state.close();
                  }}
                >
                  <Trans>Unlink</Trans>
                </Button>
              </InitialFocus>
            </View>
          </View>
        </>
      )}
    </Modal>
  );
}
